/**
 * App.js — Root application component
 * =====================================
 * Sets up the two global React contexts, persists user state to localStorage,
 * listens for auth-logout events, and defines all client-side routes.
 *
 * Contexts exported
 * ------------------
 * LocationContext  — provides { loc, setLoc } where loc = { lat, lon }.
 *                    Populated once on mount from the browser Geolocation API.
 *                    Used by Search.js and Newsfeed.js to attach coordinates
 *                    to submissions and read receipts.
 *
 * LoginContext     — provides { usr, setUsr } where `usr` holds the full user
 *                    object returned by the backend on successful login.
 *                    Persisted to localStorage so the session survives a page
 *                    refresh.
 *
 * User object shape (usr)
 * ------------------------
 * {
 *   addr      : string | null  — backend address of the user record
 *   email     : string | null  — Google account email
 *   role      : string | null  — 'ADMIN' | 'FACT_CHECKER' | 'USER'
 *   token     : string | null  — JWT session token (refreshed automatically)
 *   state     : string | null  — GIS state from user's registered location
 *   district  : string | null  — GIS district
 *   taluk     : string | null  — GIS sub-district (taluk / tehsil)
 *   states    : string[]       — list of states the user has access to
 *   adc_rank  : number | null  — current ADC cooldown value (days)
 *   adc_ts    : string | null  — ISO timestamp: when the user may submit again
 * }
 *
 * Route layout
 * ------------
 * /           → Home        — public dashboard (stats, charts, map)
 * /search     → Search      — authenticated: submit & search claims
 * /user_list  → Userslist   — authenticated: ADMIN user management
 * /news_feed  → Newsfeed    — authenticated: read & fact-check posts
 *
 * All non-public routes are wrapped in ProtectedRoute which redirects
 * unauthenticated visitors (no token) back to /.
 *
 * Auth logout flow
 * ----------------
 * apiClient dispatches a custom DOM event 'auth:logout' when a token refresh
 * fails.  The listener here resets usr to the empty state, which causes
 * ProtectedRoute to redirect away from any protected page.
 */

import { createContext, useState, useEffect, useContext } from "react";
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./pages/Layout";
import { Home } from "./pages/Home";
import { Search } from "./pages/Search";
import { Newsfeed } from "./pages/Newsfeed";
import { Userslist } from "./pages/Userslist";

// ---------------------------------------------------------------------------
// Global contexts — import these in any child component via the hooks below
// ---------------------------------------------------------------------------
export const LocationContext = createContext(null);
export const LoginContext = createContext(null);

/** Shorthand hook: access { loc, setLoc } anywhere in the tree. */
export const useUsr = () => useContext(LoginContext);

/** Shorthand hook: access { usr, setUsr } anywhere in the tree. */
export const useLoc = () => useContext(LocationContext);

// ---------------------------------------------------------------------------
// ProtectedRoute
// Redirects to / if the user is not authenticated (no JWT token).
// Wrap any Route that should not be accessible to guests.
// ---------------------------------------------------------------------------
const ProtectedRoute = ({ children, isAuthenticated }) => {
  return isAuthenticated ? children : <Navigate to="/" />;
};

// ---------------------------------------------------------------------------
// App — root component
// ---------------------------------------------------------------------------
export function App() {

  // Geolocation state — populated once from the browser Geolocation API.
  // lon/lat are in decimal degrees (WGS-84); null if permission denied or unavailable.
  const [loc, setLoc] = useState({ lat: null, lon: null });

  // User session state — initialised from localStorage on first render so the
  // session persists across page refreshes without a round-trip to the backend.
  const [usr, setUsr] = useState(() => {
    const savedUser = localStorage.getItem("usr");
    return savedUser ? JSON.parse(savedUser) : {
      addr: null,
      email: null,
      role: null,
      token: null,
      state: null,
      district: null,
      taluk: null,
      states: [],
      adc_rank: null,
      adc_ts: null
    };
  })

  useEffect(() => {

    // Request the device location once on mount.
    // lon/lat are stored in context so submit flows can attach coordinates.
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        function (position) {
          setLoc({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        function (error) {
          console.error("Geolocation error:", error.message);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      console.error("Geolocation is not available in your browser.");
    }

    // Sync usr changes to localStorage so the session survives a page refresh.
    // Passing null clears the stored entry (used after explicit logout).
    if (usr) {
      localStorage.setItem("usr", JSON.stringify(usr));
    } else {
      localStorage.removeItem("usr");
    }

    // Listen for the 'auth:logout' event dispatched by apiClient when a token
    // refresh fails.  Resetting usr triggers ProtectedRoute to redirect the
    // user away from any authenticated page.
    const handleAuthLogout = () => {
      setUsr({
        addr: null,
        email: null,
        role: null,
        token: null,
        state: null,
        district: null,
        taluk: null,
        states: [],
        adc_rank: null,
        adc_ts: null
      });
    };

    window.addEventListener('auth:logout', handleAuthLogout);

    // Cleanup: remove the event listener when the effect re-runs or unmounts.
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };

    // console.log("APP", usr)

  }, [usr]);  // Re-run whenever usr changes (login, logout, token refresh)

  return (
    <div className="App">
      <BrowserRouter>

        {/* LocationContext wraps everything so any page can read the device coordinates */}
        <LocationContext.Provider value={{ loc, setLoc }}>
          {/* LoginContext wraps everything so any page can read/write user session state */}
          <LoginContext.Provider value={{ usr, setUsr }}>

            <Routes>
              {/* Layout is a persistent shell (header + nav); child routes render via <Outlet /> */}
              <Route element={<Layout />}>

                {/* Public — accessible without login */}
                <Route exact path="/" element={<Home />} />
                {/* <Route exact path="stats" element={<Search />} /> */}

                {/* Protected — redirect to / if no token */}
                <Route
                  exact
                  path="search"
                  element={
                    <ProtectedRoute isAuthenticated={usr.token}>
                      <Search />
                    </ProtectedRoute>
                  }
                />

                <Route
                  exact
                  path="user_list"
                  element={
                    <ProtectedRoute isAuthenticated={usr.token}>
                      <Userslist />
                    </ProtectedRoute>
                  }
                />

                <Route
                  exact
                  path="news_feed"
                  element={
                    <ProtectedRoute isAuthenticated={usr.token}>
                      <Newsfeed />
                    </ProtectedRoute>
                  }
                />

              </Route>
            </Routes>

          </LoginContext.Provider>
        </LocationContext.Provider>

      </BrowserRouter>
    </div>
  );
};
